import type { ApiResponse } from './types';

const HTML_API_RELOAD_STORAGE_KEY = 'eternal-verifier:html-api-reload-at';
const HTML_API_RELOAD_QUERY_PARAM = '__ev_reload';
const HTML_API_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

function clearRecoveryReloadParam(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has(HTML_API_RELOAD_QUERY_PARAM)) {
      return;
    }

    currentUrl.searchParams.delete(HTML_API_RELOAD_QUERY_PARAM);
    window.history.replaceState(window.history.state, document.title, currentUrl.toString());
  } catch {
    // Keeping the cache-busting URL is harmless if History API is unavailable.
  }
}

clearRecoveryReloadParam();

export class ApiClientError extends Error {
  public readonly status: number;
  public readonly details: unknown;

  public constructor(status: number, message: string, details: unknown = null) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.details = details;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readStringField(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function toJsonLine(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function readPrimitiveField(source: Record<string, unknown>, key: string): string | number | boolean | null {
  const value = source[key];
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  return null;
}

function composeClientMessage(baseMessage: string, details: unknown): string {
  if (!isRecord(details)) {
    return baseMessage;
  }

  const hint = readStringField(details, 'hint');
  const debugMessage = readStringField(details, 'debugMessage');
  const diagnosticCode = readStringField(details, 'diagnosticCode') ?? readStringField(details, 'rawCode');
  const errorId = readStringField(details, 'errorId');

  const lines = [baseMessage];
  if (hint && hint !== baseMessage) {
    lines.push(hint);
  }
  if (debugMessage && debugMessage !== baseMessage && debugMessage !== hint) {
    lines.push(`Details: ${debugMessage}`);
  }

  const netDetails: Record<string, unknown> = {};
  const errno = readPrimitiveField(details, 'errno');
  const syscall = readStringField(details, 'syscall');
  const address = readStringField(details, 'address');
  const port = readPrimitiveField(details, 'port');
  if (errno != null) netDetails.errno = errno;
  if (syscall) netDetails.syscall = syscall;
  if (address) netDetails.address = address;
  if (port != null) netDetails.port = port;
  const netLine = toJsonLine(netDetails);
  if (netLine && netLine !== '{}') {
    lines.push(`Network: ${netLine}`);
  }

  const causeLine = toJsonLine(details.cause);
  if (causeLine && causeLine !== '{}') {
    lines.push(`Cause: ${causeLine}`);
  }

  const markerParts = [diagnosticCode ? `code: ${diagnosticCode}` : null, errorId ? `id: ${errorId}` : null].filter(Boolean);
  if (markerParts.length > 0) {
    lines.push(`Diagnostic: ${markerParts.join(', ')}`);
  }

  return lines.join('\n');
}

function readContentType(response: Response): string {
  return response.headers.get('content-type')?.toLowerCase() ?? '';
}

function isHtmlResponse(rawText: string, contentType: string): boolean {
  const trimmed = rawText.trimStart().toLowerCase();
  return (
    contentType.includes('text/html') ||
    trimmed.startsWith('<!doctype html') ||
    trimmed.startsWith('<html')
  );
}

function isCloudflareHtmlResponse(rawText: string, response: Response): boolean {
  const server = response.headers.get('server')?.toLowerCase() ?? '';
  const body = rawText.toLowerCase();
  return server.includes('cloudflare') || body.includes('cloudflare') || body.includes('cf-error');
}

function scheduleHtmlApiRecoveryReload(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const now = Date.now();
    const lastReload = Number(window.sessionStorage.getItem(HTML_API_RELOAD_STORAGE_KEY) ?? '0');
    if (Number.isFinite(lastReload) && now - lastReload < HTML_API_RELOAD_COOLDOWN_MS) {
      return false;
    }

    window.sessionStorage.setItem(HTML_API_RELOAD_STORAGE_KEY, String(now));
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set(HTML_API_RELOAD_QUERY_PARAM, String(now));
    window.setTimeout(() => window.location.replace(nextUrl.toString()), 50);
    return true;
  } catch {
    return false;
  }
}

async function readApiPayload<T>(response: Response): Promise<{ payload: ApiResponse<T> | null; rawText: string }> {
  const rawText = await response.text();
  if (!rawText) {
    return { payload: null, rawText };
  }

  try {
    return {
      payload: JSON.parse(rawText) as ApiResponse<T>,
      rawText
    };
  } catch {
    return { payload: null, rawText };
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  const { payload, rawText } = await readApiPayload<T>(response);

  if (!payload) {
    const statusMessage = `${response.status}${response.statusText ? ` ${response.statusText}` : ''}`.trim();
    const fallbackMessage = response.ok
      ? `Invalid API response (${statusMessage || 'unknown status'})`
      : `Request failed (${statusMessage || 'unknown status'})`;
    const rawBody = rawText.trim();
    const contentType = readContentType(response);
    const htmlResponse = isHtmlResponse(rawBody, contentType);
    const cloudflareResponse = htmlResponse && isCloudflareHtmlResponse(rawBody, response);
    const retryable = response.status >= 500;
    const autoReloadScheduled = htmlResponse && retryable ? scheduleHtmlApiRecoveryReload() : false;
    const details = rawBody
      ? {
          rawBody: rawBody.slice(0, 500),
          contentType: contentType || null,
          htmlResponse,
          cloudflareResponse,
          retryable,
          autoReloadScheduled
        }
      : null;

    if (htmlResponse) {
      const message = autoReloadScheduled
        ? 'The server returned an HTML error page instead of API JSON. Reloading the app once to fetch the latest version.'
        : cloudflareResponse
          ? 'The server returned a Cloudflare error page instead of API JSON. Refresh the page and try again.'
          : 'The server returned an HTML page instead of API JSON. Refresh the page and try again.';
      throw new ApiClientError(response.status, message, details);
    }

    const bodyPreview = rawBody ? rawBody.replace(/\s+/g, ' ').slice(0, 220) : '';
    const message = bodyPreview ? `${fallbackMessage}. Body: ${bodyPreview}` : fallbackMessage;
    throw new ApiClientError(response.status, message, details);
  }

  if (!response.ok || !payload.success) {
    const message = composeClientMessage(payload.error ?? 'Request failed', payload.details ?? null);
    throw new ApiClientError(response.status, message, payload.details ?? null);
  }

  return payload.data;
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  const response = await fetch(path, {
    ...options,
    cache: options.cache ?? 'no-store',
    credentials: 'same-origin',
    headers
  });

  return parseResponse<T>(response);
}

export const api = {
  get<T>(path: string): Promise<T> {
    return apiRequest<T>(path, { method: 'GET' });
  },
  post<T>(path: string, body?: unknown | FormData): Promise<T> {
    return apiRequest<T>(path, {
      method: 'POST',
      body: body == null ? undefined : body instanceof FormData ? body : JSON.stringify(body)
    });
  },
  put<T>(path: string, body?: unknown | FormData): Promise<T> {
    return apiRequest<T>(path, {
      method: 'PUT',
      body: body == null ? undefined : body instanceof FormData ? body : JSON.stringify(body)
    });
  },
  delete<T>(path: string): Promise<T> {
    return apiRequest<T>(path, { method: 'DELETE' });
  }
};
